// client/src/api/blobStorageAPI.js

import axiosInstance from '../utilities/axiosInstance';

const blobStorageAPI = {
  // ============================================================================
  // FILE DOWNLOAD & VIEW
  // ============================================================================
  
  // Get download URL (helper method) - NO encoding, route uses wildcard
  getFileDownloadUrl: (fileId) => {
    return `/api/files/download/${fileId}`;
  },

  // Get view URL (helper method) - NO encoding, route uses wildcard
  getFileViewUrl: (fileId) => {
    return `/api/files/view/${fileId}`;
  },

  // Download file
  downloadFile: async (fileId, requestedBy) => {
    try {
      const response = await axiosInstance.get(`/api/files/download/${fileId}`, {
        params: { requestedBy },
        responseType: 'blob'
      });
      return { success: true, data: response.data };
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to download file: ${error.message}` };
    }
  },

  // View file
  viewFile: async (fileId, requestedBy) => {
    try {
      const response = await axiosInstance.get(`/api/files/view/${fileId}`, {
        params: { requestedBy },
        responseType: 'blob'
      });
      return { success: true, data: response.data };
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to view file: ${error.message}` };
    }
  },

  // Get file metadata only (without downloading)
  getFileMetadata: async (fileId, requestedBy) => {
    try {
      const response = await axiosInstance.get(`/api/files/metadata/${fileId}`, {
        params: { requestedBy }
      });
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get file metadata: ${error.message}` };
    }
  },

  // ============================================================================
  // FILE RETRIEVAL
  // ============================================================================

  // Get attachments with filters
  getAttachments: async (filters = {}) => {
    try {
      const response = await axiosInstance.get('/api/files/attachments', {
        params: filters
      });
      return response.data; // {success, data, count}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get attachments: ${error.message}` };
    }
  },

  // Get student assessment attachments
  getStudentAssessmentAttachments: async (studentId, assessmentType, assessmentId, courseId) => {
    try {
      const response = await axiosInstance.get(
        `/api/files/student/${studentId}/assessment/${assessmentType}/${assessmentId}`,
        { params: { courseId } }
      );
      return response.data; // {success, data, count}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get assessment attachments: ${error.message}` };
    }
  },

  // Get question attachments
  getQuestionAttachments: async (studentId, questionId, courseId, assessmentType, assessmentId) => {
    try {
      const response = await axiosInstance.get(
        `/api/files/student/${studentId}/question/${questionId}`,
        { params: { courseId, assessmentType, assessmentId } }
      );
      return response.data; // {success, data, count}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get question attachments: ${error.message}` };
    }
  },

  // Get course attachments
  getCourseAttachments: async (courseId) => {
    try {
      const response = await axiosInstance.get(`/api/files/course/${courseId}`);
      return response.data; // {success, data, count}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get course attachments: ${error.message}` };
    }
  },

  // Get student attachments
  getStudentAttachments: async (studentId) => {
    try {
      const response = await axiosInstance.get(`/api/files/student/${studentId}`);
      return response.data; // {success, data, count}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get student attachments: ${error.message}` };
    }
  },

  // Get attachments by uploader
  getAttachmentsByUploader: async (uploadedBy) => {
    try {
      const response = await axiosInstance.get(`/api/files/uploader/${uploadedBy}`);
      return response.data; // {success, data, count}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get uploader attachments: ${error.message}` };
    }
  },

  // Get file statistics
  getFileStatistics: async (filters = {}) => {
    try {
      const response = await axiosInstance.get('/api/files/statistics', {
        params: filters
      });
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get file statistics: ${error.message}` };
    }
  },

  // ============================================================================
  // FILE UPLOAD
  // ============================================================================

  // Upload single file
  uploadFile: async (file, metadata) => {
      const formData = new FormData();
      formData.append('file', file);
      
      // ✅ ALL metadata fields are appended
      formData.append('studentId', metadata.studentId);
      formData.append('loginId', metadata.loginId);              // ✅ Add this!
      formData.append('courseId', metadata.courseId);
      formData.append('assessmentType', metadata.assessmentType);
      formData.append('assessmentId', metadata.assessmentId);
      formData.append('questionId', metadata.questionId);
      formData.append('attemptNumber', metadata.attemptNumber);   // ✅ Add this!
      formData.append('uploadedBy', metadata.uploadedBy);
      formData.append('originalFilename', metadata.originalFilename);  // ✅ Add this!
      formData.append('structuredFilename', metadata.structuredFilename);  // ✅ Add this!

      const response = await axiosInstance.post('/api/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
  },

  // Upload multiple files
  uploadMultipleFiles: async (files, metadata) => {
    try {
      const formData = new FormData();
      
      // Append all files
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      
      // Append metadata
      formData.append('studentId', metadata.studentId);
      formData.append('courseId', metadata.courseId);
      formData.append('assessmentType', metadata.assessmentType);
      formData.append('assessmentId', metadata.assessmentId);
      formData.append('questionId', metadata.questionId);
      if (metadata.uploadedBy) {
        formData.append('uploadedBy', metadata.uploadedBy);
      }

      const response = await axiosInstance.post('/api/files/upload-multiple', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data; // {success, data, errors, message}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to upload files: ${error.message}` };
    }
  },

  // ============================================================================
  // FILE DELETION
  // ============================================================================

  // Delete single file - NO encoding, route uses wildcard
  deleteFile: async (fileId, deletedBy) => {
    try {
      const response = await axiosInstance.delete(`/api/files/${fileId}`, {
        data: { deletedBy }
      });
      return response.data; // {success, message}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to delete file: ${error.message}` };
    }
  },

  // Delete multiple files
  deleteMultipleFiles: async (fileIds, deletedBy) => {
    try {
      const response = await axiosInstance.delete('/api/files/bulk', {
        data: { fileIds, deletedBy }
      });
      return response.data; // {success, data, errors, message}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to delete files: ${error.message}` };
    }
  },

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  // Helper to trigger browser download
  triggerDownload: async (fileId, filename, requestedBy) => {
    try {
      const result = await blobStorageAPI.downloadFile(fileId, requestedBy);
      if (!result.success) {
        throw new Error(result.error);
      }

      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, message: 'File downloaded successfully' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Helper to open file in new tab
  openFileInNewTab: (fileId) => {
    const url = blobStorageAPI.getFileViewUrl(fileId);
    window.open(url, '_blank');
  },

  /**
   * List all knowledge posts (get count and filenames)
   * @returns {Promise<{success: boolean, number?: number, files?: string[], error?: string}>}
   */
  listKnowledgePosts: async () => {
    try {
      const response = await axiosInstance.post('/api/files/notes/list', {
        containerType: 'knowledge'
      });
      return response.data; // {success, number, files}
    } catch (error) {
      return error?.response?.data || { 
        success: false, 
        error: `Failed to list knowledge posts: ${error.message}` 
      };
    }
  },

  /**
   * Load all knowledge posts with full content
   * @param {number} count - Number of posts to load
   * @returns {Promise<{success: boolean, files?: Array, error?: string}>}
   */
  loadKnowledgePosts: async (count) => {
    try {
      const response = await axiosInstance.post('/api/files/notes/listAll', {
        containerType: 'knowledge',
        number: count
      });
      return response.data; // {success, files: [{filename, content, timestamp, loginId, status, metadata}]}
    } catch (error) {
      return error?.response?.data || { 
        success: false, 
        error: `Failed to load knowledge posts: ${error.message}` 
      };
    }
  },

  /**
   * Get new knowledge posts above threshold (for notifications)
   * @param {number} tagNumberThreshold - Only return posts with tag number greater than this
   * @returns {Promise<{success: boolean, files?: string[], error?: string}>}
   */
  getNewKnowledgePosts: async (tagNumberThreshold) => {
    try {
      const response = await axiosInstance.post('/api/files/notes/listByTagNumber', {
        containerType: 'knowledge',
        tagNumberThreshold
      });
      return response.data; // {success, files: []}
    } catch (error) {
      return error?.response?.data || { 
        success: false, 
        error: `Failed to get new knowledge posts: ${error.message}` 
      };
    }
  },

  /**
   * Create a new knowledge post (Admin only)
   * @param {Object} postData - Post data
   * @param {string} postData.message - Full content including subject
   * @param {string} postData.loginId - User ID creating the post
   * @param {string} postData.filename - Filename for the post
   * @param {string} [postData.senderName] - Name of the user (optional)
   * @returns {Promise<{success: boolean, message?: string, error?: string}>}
   */
  createKnowledgePost: async (postData) => {
    try {
      const response = await axiosInstance.post('/api/files/notes/create', {
        containerType: 'knowledge',
        message: postData.message,
        loginId: postData.loginId,
        filename: postData.filename,
        senderName: postData.senderName
      });
      return response.data; // {success, message}
    } catch (error) {
      return error?.response?.data || { 
        success: false, 
        error: `Failed to create knowledge post: ${error.message}` 
      };
    }
  },

  /**
   * Update an existing knowledge post (Admin only)
   * @param {string} loginId - User ID performing the update
   * @param {string} filename - Filename of the post to update
   * @param {Object} updateData - Update data
   * @param {string} updateData.message - Updated content
   * @param {string} [updateData.status] - Status (optional, defaults to 'Updated')
   * @returns {Promise<{success: boolean, message?: string, error?: string}>}
   */
  updateKnowledgePost: async (loginId, filename, updateData) => {
    try {
      const response = await axiosInstance.put(
        `/api/files/notes/update/${loginId}`,
        {
          containerType: 'knowledge',
          message: updateData.message,
          status: updateData.status || 'Updated'
        },
        {
          params: { filename }
        }
      );
      return response.data; // {success, message}
    } catch (error) {
      return error?.response?.data || { 
        success: false, 
        error: `Failed to update knowledge post: ${error.message}` 
      };
    }
  },

  /**
   * Delete a knowledge post (Admin only)
   * @param {string} loginId - User ID performing the deletion
   * @param {string} filename - Filename of the post to delete
   * @returns {Promise<{success: boolean, message?: string, error?: string}>}
   */
  deleteKnowledgePost: async (loginId, filename) => {
    try {
      const response = await axiosInstance.delete(
        `/api/files/notes/delete/${loginId}`,
        {
          params: { filename },
          data: { containerType: 'knowledge' }
        }
      );
      return response.data; // {success, message}
    } catch (error) {
      return error?.response?.data || { 
        success: false, 
        error: `Failed to delete knowledge post: ${error.message}` 
      };
    }
  },

};



export default blobStorageAPI;